'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { motion } from 'framer-motion'
import FAQreferral from '@/components/ReferralFAQ'
import FullBleedSection from '@/components/FullBleedSection'

const HeroSection = () => {
  return (
    <div>
      {/* Hero Image Section */}
      <div className="relative min-h-screen"></div>
      <div className="absolute inset-0 -z-10">
        <Image
          src="/images/referral-hero.jpeg"
          alt="Referral Page Desktop"
          quality={100}
          fill
          priority
          className="object-cover"
          sizes="100vw"
        />
        <div className="absolute inset-0 bg-black/50" />
      </div>
      {/* Mobile Image */}
      <div className="absolute inset-0 -z-10 block md:hidden">
        <Image
          src="/images/referral-vertical.jpg"
          alt="Referral Page Mobile"
          quality={100}
          fill
          priority
          className="object-cover object-center"
          sizes="100vw"
        />
        <div className="absolute inset-0 bg-black/40" />
      </div>

      {/* Hero Text */}
      <div className="absolute inset-0 z-10 md:px-4 lg:px-0">
        <div className="relative z-10 mx-auto max-w-7xl">
          <div className="flex min-h-screen flex-col px-4 pt-32 sm:block sm:px-6 md:px-8 lg:px-10">
            {/* Hero Content */}
            <div className="flex flex-grow flex-col justify-center pb-8 text-center sm:absolute sm:inset-0 sm:mb-60 md:pl-0 md:text-left">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h1 className="mb-6 text-4xl font-bold text-white md:text-5xl lg:text-6xl">
                  GoDeutschland <span className="text-[#ff4500]"> empfehlen</span>
                </h1>
                <h1 className="mt-2 text-2xl font-bold text-white sm:text-3xl md:text-4xl">
                  Erhalten Sie bis zu 500 € <br />
                  <span className="mx-auto max-w-2xl text-xl font-medium text-white md:font-bold md:text-[#ff4500]">
                    für jeden Kunden, den Sie erfolgreich angeworben haben.
                  </span>
                </h1>
                <p className="my-8 hidden text-lg text-white md:block">
                  Empfehlen Sie GoDeutschland an Familie, Freunde und Nachbarn und geben
                  gleichzeitig einen Rabatt weiter. Sie können uns empfehlen, ganz unabhängig davon,
                  ob Sie GoDeutschland Kunde sind oder nicht.&nbsp;
                  <a
                    href="/teilnahmebedingungen"
                    className="font-semibold text-[#ff4500] underline transition hover:text-white"
                  >
                    Hier
                  </a>
                  &nbsp;finden Sie die Teilnahmebedingungen.
                </p>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="my-8 md:my-0"
                >
                  <Link
                    href="#linkformular"
                    className="inline-block rounded-lg bg-[#ff4500] px-8 py-4 font-bold text-white transition-all hover:scale-105 hover:bg-[#e03e00]"
                    onClick={(e) => {
                      e.preventDefault()
                      document.getElementById('linkformular')?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      })
                    }}
                  >
                    Prämie sichern
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
// Data for the steps
const steps = [
  {
    number: '1',
    title: 'Link teilen',
    description:
      'Schicken Sie Ihren persönlichen Empfehlungslink an die Personen, die sich für eine GoDeutschland Volks-Photovoltaik oder Volks-Isolierung interessieren könnte. ',
  },
  {
    number: '2',
    title: 'Kunden werben',
    description:
      'Die empfohlene Person meldet sich bei uns über Ihren Link an und erhält sowohl eine kostenlose Planung als auch eine maximale Prämie von 500 €, wenn die Installation erfolgreich durchgeführt wird. ',
  },
  {
    number: '3',
    title: 'Prämie erhalten',
    description:
      'Sie erhalten eine Prämie in Höhe von maximal 500 € (Photovoltaik = 200 € und Isolierung = 300 €) auf Ihr Konto ausgezahlt, nachdem die Zahlung der empfohlenen Person erfolgt ist.',
  },
]

// Component to render the steps grid
function ReferStepsGrid() {
  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3 md:px-4 lg:px-0">
      {steps.map((step, idx) => (
        <div
          key={idx}
          className="relative mx-auto flex flex-col items-center rounded-lg border border-[#ff4500]/10 bg-white p-6 text-center shadow-md hover:scale-103 hover:shadow-lg md:max-w-xl md:items-start md:text-left"
        >
          {/* Number */}
          <div className="flex items-center gap-3">
            <div className="text-4xl font-bold text-[#ff4500]">{step.number}</div>
          </div>

          {/* Title & Description */}
          <h3 className="mt-4 text-xl font-semibold">{step.title}</h3>
          <p className="mt-2 text-gray-700">{step.description}</p>

          {/* Arrow between cards on desktop */}
          {idx < steps.length - 1 && (
            <div className="absolute top-1/2 -right-8 hidden -translate-y-1/2 transform md:flex">
              <svg
                className="h-8 w-8 text-[#ff4500]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// Form component
const ReferForm = () => {
  const [formData, setFormData] = useState({ name: '', email: '' })
  const [referralLink, setReferralLink] = useState('')
  const [copied, setCopied] = useState(false)

  // Add base URL for absolute path
  const baseUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://godeutschland.de' // Update with your domain
  const baseLandingPath = '/termin'

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const { name, email } = formData
    // Create absolute URL for the referral link
    const link = `${baseUrl}${baseLandingPath}?ref=hero&name=${encodeURIComponent(
      name
    )}&email=${encodeURIComponent(email)}`
    setReferralLink(link)
    setTimeout(() => {
      const element = document.querySelector('.referral-success')
      if (element) {
        const elementRect = element.getBoundingClientRect()
        const absoluteElementTop = elementRect.top + window.pageYOffset
        const middle = absoluteElementTop - window.innerHeight / 2 + elementRect.height / 2
        window.scrollTo({
          top: middle,
          behavior: 'smooth',
        })
      }
    }, 100)
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000) // Reset copied state after 2 seconds
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }
  if (referralLink) {
    if (referralLink) {
      return (
        <div className="referral-success rounded-lg bg-[#e9e9e6] p-6 text-center">
          <p className="text-xl font-medium text-black">
            Hier ist Ihr persönlicher Empfehlungslink:
          </p>
          <div className="mt-4 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <a
              href={referralLink}
              className="block w-full overflow-hidden text-lg font-semibold text-ellipsis whitespace-nowrap text-[#ff4500] underline sm:w-auto"
            >
              {referralLink}
            </a>
            <button
              onClick={handleCopyLink}
              className="inline-flex w-full cursor-pointer items-center justify-center rounded-lg bg-[#ff4500] px-4 py-2 font-medium text-white transition hover:bg-black sm:w-auto"
            >
              {copied ? (
                <>
                  <svg
                    className="mr-2 h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Kopiert!
                </>
              ) : (
                <>
                  <svg
                    className="mr-2 h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                    />
                  </svg>
                  Link kopieren
                </>
              )}
            </button>
          </div>
          <p className="text-s mt-4 text-gray-700">
            *Speichern Sie Ihren eindeutigen Link. Nach dem Neuladen der Seite oder beim Verlassen
            der Seite verschwindet er!
          </p>
        </div>
      )
    }
  }

  return (
    <div className="mx-auto max-w-7xl md:px-4 lg:px-0">
      <FullBleedSection className="mb-8 bg-[#212121] py-5 md:hidden">
        <h1 className="mb-2 text-center text-2xl font-bold text-white md:hidden">
          Empfehlen Sie {''}
          <span className="text-[#ff4500]">GoDeutschland</span>
        </h1>
        <p className="mt-8 text-center font-medium text-white md:hidden">
          Empfehlen Sie GoDeutschland an Familie, Freunde und Nachbarn und geben gleichzeitig einen
          Rabatt weiter. Sie können uns empfehlen, ganz unabhängig davon, ob Sie GoDeutschland Kunde
          sind oder nicht.&nbsp;
          <a
            href="/teilnahmebedingungen"
            className="font-semibold text-[#ff4500] underline transition hover:text-white"
          >
            Hier
          </a>
          &nbsp;finden Sie die Teilnahmebedingungen.
        </p>
      </FullBleedSection>
      {/* Form Section */}
      <div id="linkformular" className="grid grid-cols-1 gap-8 lg:grid-cols-[400px_1fr]">
        {/* Left: Image */}
        <div className="hidden lg:block">
          <Image
            src="/images/referral-vertical.jpg"
            className="h-full rounded-xl"
            alt="Enpal Solarhelden Illustration"
            width={700}
            height={400}
            loading="eager"
          />
        </div>

        {/* Right: Two stacked cards */}
        <div className="flex flex-col gap-8">
          {/* Card: Text */}
          <div className="rounded-lg p-0 md:p-8">
            <h3 className="text-center text-xl font-bold sm:text-3xl md:text-4xl">
              Einfach und schnell mitmachen
            </h3>
          </div>
          {/* Card: Form */}
          <div className="flex-1 rounded-lg bg-[#e9e9e6] p-6 shadow-md">
            <h3 className="text-2xl font-semibold">
              Für jeden empfohlenen GoDeutschland Kunden erhalten Sie eine Prämie in Höhe von
              maximal 500 €.
            </h3>
            <p className="mt-2 text-base font-medium text-gray-600">
              Um sofort Ihren persönlichen Empfehlungslink zu bekommen, tragen Sie einfach Ihre
              E-Mail-Adresse ein.
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              {/* name field */}
              <div className="space-y-2">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Vor- und Nachname
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Ihr Name"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm transition focus:border-[#ff4500] focus:ring-2 focus:ring-[#ff4500] focus:outline-none"
                />
              </div>
              {/* email field */}
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  E-Mail Adresse
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@beispiel.de"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm transition focus:border-[#ff4500] focus:ring-2 focus:ring-[#ff4500] focus:outline-none"
                />
              </div>
              {/* submit */}
              <button
                type="submit"
                className="inline-flex w-fit cursor-pointer items-center justify-center rounded-lg bg-[#ff4500] px-6 py-3 font-medium text-white transition-all hover:scale-105 hover:bg-[#e03e00]"
              >
                Empfehlungslink erhalten
                <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </button>
            </form>

            <p className="mt-4 text-xs text-gray-500">
              *Die Empfehlungsprämie kann nur ausgezahlt werden, wenn die Empfohlenen die Anfrage
              über den persönlichen Empfehlungslink stellen. Es gelten die{' '}
              <a
                href="/teilnahmebedingungen"
                className="text-[#ff4500] underline hover:text-[#212121]"
              >
                Teilnahmebedingungen
              </a>
              . Dort finden Sie auch die Informationen zum Datenschutz.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// Combined export
export default function ReferSection() {
  return (
    <>
      <HeroSection />
      <section className="mx-auto max-w-7xl space-y-8 py-10">
        <ReferForm />
        <ReferStepsGrid />
        <FAQreferral />
      </section>
    </>
  )
}
